import {cx} from '@local/utils/cx'
import {PortableText as SanityPortableText} from '@portabletext/react'
import {Button} from '../button/Button'
import {ElementType, forwardRef} from 'react'
import {slugify} from '@local/utils/string'
// import {useScrollStore} from '@local/stores'
import type {PortableTextBlock} from '@portabletext/types'
import {SanityImage, Blockquote as BlockquoteComponent, Gallery as GalleryComponent} from '@local/ui/components'

type BlockContentOptions = {
	noDom?: boolean
	styles?: Record<string, string>
	withHeadingAnchorLink?: boolean
	imageDesktopWidth?: number
	imageMobileWidth?: number
}

export type PortableTextProps = BlockContentOptions & {
	className?: string
	blocks: PortableTextBlock[]
	as?: ElementType
	withHeadingAnchorLink?: boolean
	noDefaultStyles?: boolean
}

export const defaultStyles = {
	normalStyle: 'text-10 my-10 first:mt-0 last:mb-0',
	listStyle: 'text-grey-dark text-10 mt-30',
	listBulletStyle: 'list-disc uppercase [&_button]:text-black [&_li]:mb-2 [&>li]:gutter-ml-1 [&_li]:marker:text-black',
	listNumberStyle: cx(
		// V1
		'list-none [&_li]:marker:text-black uppercase mt-30 [&_strong]:font-normal [&_strong]:text-black',
		'[counter-reset:item] [&_li]:before:inline-block [&_li]:before:span-w-1-wide [&_li]:before:text-black [&_li]:before:[content:counters(item,".")"."] [&_li]:[counter-increment:item]',
		// V2
		// 'list-none [counter-reset:item] [&_li]:before:inline-block [&_li]:before:w-60 [&_li]:before:[content:counters(item,".")"."] [&_li]:[counter-increment:item] [&_li]:before:text-black [&_li]:marker:text-black [&>li]:span-ml-1-wide uppercase mt-30 [&_strong]:font-normal [&_strong]:text-black',
		// '[counter-reset:item] [&_li]:before:inline-block [&_li]:before:span-w-1-wide [&_li]:before:text-black [&_li]:before:[content:counters(item,".")"."] [&_li]:[counter-increment:item]',
		// '[counter-reset:item] [&_li]:marker:[content:counters(item,".")"."] [&_li]:[counter-increment:item] [&>li]:pl-40',
	),
	// listNumberStyle: `list-none [counter-reset:section] [&>li]:[counter-increment:section] [&>li]:marker:[content:counters(section,'.')] uppercase mt-30`,
	listItemStyle: 'mb-30 last:mb-0 lg:span-w-9',
	h2: 'text-10 uppercase font-medium mt-40 mb-30 first:mt-0 last:mb-0',
	h3: 'font-serif text-10 mt-40 mb-30 first:mt-0 last:mb-0',
	tableStyle: 'align-left uppercase text-grey text-left',
	tableRowStyle: 'text-9 text-grey-dark text-20 leading-tight',
	'tableRowStyle-0': '!text-12 !text-black uppercase font-medium tracking-wide',
	tableCellStyle: 'border border-grey-dark p-15 uppercase',
	imageStyle: 'my-60',
}

const Block = ({
	noDom = false,
	styles = {},
	withHeadingAnchorLink,
}: BlockContentOptions) => {
	const BlockCurry = (props) => {
		const {style = 'normal'} = props.node

		if (noDom) {
			return props.children
		} else if (/^h\d/.test(style)) {
			const HeadingTag = style
			const slug = slugify(props.node.children[0].text)
			// headings
			return (
				<HeadingTag
					id={slug}
					className={styles[style]}>
					{withHeadingAnchorLink
						? (
							<a
								href={`#${slug}`}>
								{props.children}
							</a>
						)
						: props.children}
				</HeadingTag>
			)
		} else if (style === 'blockquote') {
			// blockquotes
			return <blockquote className={styles.blockquote}>{props.children}</blockquote>
		} else if (style === 'normal') {
			return <p className={styles.normal || styles.normalStyle}>{props.children}</p>
		}
		return <p className={styles[style]}>{props.children}</p>
	}

	return BlockCurry
}

const ImageAlt = ({noDom = false, styles = {}, imageDesktopWidth, imageMobileWidth}: BlockContentOptions) => ({value}) => {
	if (noDom) {
		return value.asset._ref
	}
	return <SanityImage className={styles.imageStyle} image={value} alt={value.alt} desktopWidth={imageDesktopWidth} mobileWidth={imageMobileWidth} />
}

const Table = ({styles = {}}: BlockContentOptions) => (table) => {
	return (
		<table className={cx(styles.tableStyle)}>
			<tbody>
				{table.value.rows.map((row, i) => (
					<tr
						key={row._key}
						className={cx(
							styles.tableRowStyle,
							styles[`tableRowStyle-${i}`],
						)}>
						{row.cells.map((cell, j) => (
							<td
								key={`${i}-${j}`}
								className={cx(
									styles.tableCellStyle,
									styles[`tableColumnStyle-${j}`],
									styles[`tableCellStyle-${i}-${j}`],
								)}>
								{cell}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	)
}

const Blockquote = ({styles}) => (props) => {
	const {value: quote} = props
	return (
		<BlockquoteComponent {...quote} />
	)
}

const Gallery = ({styles}) => (props) => {
	const {value: gallery} = props
	return <GalleryComponent slides={gallery.slides} />
}

const List = ({noDom = false, styles = {}}: BlockContentOptions) => ({
	bullet: ({children = undefined}) => noDom ? children : <ul className={cx(styles.listStyle, styles.listBulletStyle)}>{children}</ul>,
	number: ({children = undefined}) => noDom ? children : <ol className={cx(styles.listStyle, styles.listNumberStyle)}>{children}</ol>,
})

const ListItem = ({noDom = false, styles = {}}: BlockContentOptions) => ({
	bullet: ({children = undefined}) => noDom ? children : <li className={cx(styles.listItemStyle)}>{children}</li>,
	number: ({children = undefined}) => noDom ? children : <li className={cx(styles.listItemStyle)}>{children}</li>,
})

const serializers = ({noDom = false, styles = {}, withHeadingAnchorLink, imageDesktopWidth, imageMobileWidth}: BlockContentOptions) => ({
	block: Block({noDom, styles, withHeadingAnchorLink}),
	list: List({noDom, styles}),
	listItem: ListItem({noDom, styles}),
	types: {
		imageLocaleAlt: ImageAlt({noDom, styles, imageDesktopWidth, imageMobileWidth}),
		table: Table({noDom, styles}),
		blockquote: Blockquote({styles}),
		gallery: Gallery({styles}),
	},
	marks: {
		em: ({children}) => noDom ? children : <em className={styles.emClassName}>{children}</em>,
		strong: ({children}) => noDom ? children : <strong className={styles.strongClassName}>{children}</strong>,
		link: ({children, value}) => {
			if (noDom) return children
			return (
				<Button linkInput={value.href} variant='classicUnderlined' prefetch={false}>
					{children}
				</Button>
			)
		},
	},
})

export const SanityBlockContent = forwardRef(({
	as = 'div',
	blocks,
	noDom,
	styles,
	imageDesktopWidth = 440,
	imageMobileWidth = 390,
	noDefaultStyles,
	className,
	withHeadingAnchorLink = false,
}: PortableTextProps, ref) => {
	if (!blocks) return null
	const Tag = as
	const components = serializers({
		noDom,
		styles: noDefaultStyles ? styles : {...defaultStyles, ...styles},
		withHeadingAnchorLink,
		imageDesktopWidth,
		imageMobileWidth,
	})

	return (
		<Tag ref={ref} className={className}>
			<SanityPortableText
				value={blocks}
				components={components} />
		</Tag>
	)
})
